import { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import useTranslation from '../../hooks/useTranslation'
import ModalCard from '../modal/ModalCard'
import { Icon_Plus, Icon_Arrow_Left_Simple, Icon_People, Icon_Kanban } from '../../styles/icons'
import ConnectField from '../select/ConnectField'
import { media } from '../../styles/media'

const Header = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 1rem;
    padding: 1rem 1.25rem 0.75rem;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ theme }) => theme.card.background};
    flex-shrink: 0;
    border-radius: 1rem 1rem 0 0;
`

const BackButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    padding: 0.2rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    flex-shrink: 0;
    transition: color 0.15s, background 0.15s;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.form.panelBg};
    }
`

const SelectorGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    flex-shrink: 1;

    ${media.phone} {
        display: ${({ $mobileHidden }) => $mobileHidden ? 'none' : 'flex'};
    }
`

const ConnectWrapper = styled.div`
    width: ${({ $width }) => $width ?? '360px'};
    flex-shrink: 1;
    min-width: 0;
`

const MobileToggle = styled.button`
    display: none;
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    align-items: center;
    padding: 0.2rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    flex-shrink: 0;
    transition: color 0.15s, background 0.15s;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.form.panelBg};
    }

    ${media.phone} {
        display: flex;
    }
`

const AddButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
    padding: 0.2rem;
    border-radius: 0.25rem;
    font-size: 1rem;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;

    &:hover {
        color: ${({ theme }) => theme.font.color};
        background: ${({ theme }) => theme.form.panelBg};
    }
`

const KanbanHeader = ({ selectorValue, selectedParent, selectedParentId, onParentChange, assigneeValue, onAssigneeChange, onTaskCreated, headerConfig, areaConfig }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [modalOpen, setModalOpen] = useState(false)
    const [mobileSelector, setMobileSelector] = useState('project')

    const { selector, assigneeFilter } = headerConfig
    const defaultSwimlane = areaConfig.swimlanes.find(s => s.statusValue === areaConfig.defaultStatus)

    const handleModalClose = () => {
        setModalOpen(false)
        onTaskCreated()
    }

    const toggleMobileSelector = () => {
        setMobileSelector(prev => prev === 'project' ? 'assignee' : 'project')
    }

    return (
        <Header>
            <BackButton onClick={() => navigate(-1)}>{Icon_Arrow_Left_Simple}</BackButton>
            <SelectorGroup $mobileHidden={assigneeFilter && mobileSelector !== 'project'}>
                <ConnectWrapper $width={selector.width}>
                    <ConnectField config={selector} value={selectorValue} onChange={onParentChange} />
                </ConnectWrapper>
            </SelectorGroup>

            {assigneeFilter && (
                <SelectorGroup $mobileHidden={mobileSelector !== 'assignee'}>
                    <ConnectWrapper $width={assigneeFilter.width}>
                        <ConnectField config={assigneeFilter} value={assigneeValue} onChange={onAssigneeChange} />
                    </ConnectWrapper>
                </SelectorGroup>
            )}

            {assigneeFilter && (
                <MobileToggle onClick={toggleMobileSelector} title={mobileSelector === 'project' ? t(assigneeFilter.placeholder) : t(selector.placeholder)}>
                    {mobileSelector === 'project' ? Icon_People : Icon_Kanban}
                </MobileToggle>
            )}

            {selectedParentId && (
                <AddButton onClick={() => setModalOpen(true)} title={t('kanban.swimlane.add.task')}
                    data-testid={`kanban.${areaConfig.subEntity}.add`}>
                    {Icon_Plus}
                </AddButton>
            )}

            {modalOpen && (
                <ModalCard
                    isVisible={true}
                    setIsVisible={setModalOpen}
                    entity={areaConfig.subEntity}
                    origin={{ id: selectedParentId }}
                    mode="new"
                    testId={`kanban.${areaConfig.subEntity}.modal`}
                    load={false}
                    onClose={handleModalClose}
                    effect={handleModalClose}
                    defaultValues={{
                        status:     areaConfig.defaultStatus,
                        priority:   areaConfig.defaultPriority,
                        statusName: t(defaultSwimlane?.title ?? ''),
                        stackRank:  areaConfig.defaultStackRank
                    }}
                />
            )}
        </Header>
    )
}

export default KanbanHeader
